import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Store, Users, Search, Filter, Clock, Plus, PlusCircle, 
  Edit, Trash2, XCircle, CheckCircle, Copy, Settings,
  ChevronDown, Bell, Eye, EyeOff, DollarSign, Zap, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './components/ui/dropdown-menu';

import { getBackendUrl } from './utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// 🆕 Componente de Timer de Ocupação
const OccupationTimer = ({ startTime }) => {
  const [elapsed, setElapsed] = useState('');
  const [urgency, setUrgency] = useState('normal'); // normal, warning, critical

  useEffect(() => {
    if (!startTime) return;

    const updateTimer = () => {
      const start = new Date(startTime);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000); // seconds

      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);

      const timeStr = hours > 0 
        ? `${hours}h ${minutes}min` 
        : `${minutes}min`;

      setElapsed(timeStr);

      // Define urgência baseado no tempo
      if (diff > 3600) {
        setUrgency('critical'); // Mais de 1 hora
      } else if (diff > 2700) {
        setUrgency('warning'); // Mais de 45 minutos
      } else {
        setUrgency('normal');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 30000); // Atualiza a cada 30 segundos

    return () => clearInterval(interval);
  }, [startTime]);

  const getTimerStyle = () => {
    switch (urgency) {
      case 'critical':
        return 'bg-red-500/20 text-red-400 border-red-500/40 animate-pulse';
      case 'warning':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40';
      default:
        return 'bg-gray-700/50 text-gray-400 border-gray-600/40';
    }
  };

  if (!elapsed) return null;

  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-medium ${getTimerStyle()}`}>
      <Clock className="w-3 h-3" />
      <span>{elapsed}</span>
    </div>
  );
};

// 🆕 Componente de Dashboard Resumido (Colapsável)
const TablesDashboard = ({ tables }) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    // Carrega preferência do localStorage
    const saved = localStorage.getItem('dashboardExpanded');
    return saved === null ? true : saved === 'true';
  });

  const stats = {
    total: tables.length,
    free: tables.filter(t => t.status === 'livre' && !t.has_order).length,
    occupied: tables.filter(t => t.status === 'ocupada' || t.has_order).length,
    reserved: tables.filter(t => t.status === 'reservada').length,
    totalRevenue: tables
      .filter(t => t.has_order && t.current_order)
      .reduce((sum, t) => sum + (t.current_order.total || 0), 0)
  };

  const occupancyRate = stats.total > 0 
    ? Math.round((stats.occupied / stats.total) * 100) 
    : 0;

  const toggleDashboard = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    localStorage.setItem('dashboardExpanded', newState.toString());
  };

  return (
    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-700/50 mb-6 overflow-hidden transition-all duration-300">
      {/* Header - Sempre visível */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/30 transition-colors"
        onClick={toggleDashboard}
        data-testid="dashboard-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              Visão Geral
              {!isExpanded && (
                <span className="text-xs font-normal text-gray-400 bg-gray-700/50 px-2 py-0.5 rounded-full">
                  {stats.free}L • {stats.occupied}O • R$ {stats.totalRevenue.toFixed(0)}
                </span>
              )}
            </h3>
            <p className="text-gray-400 text-xs">
              {isExpanded ? 'Status em tempo real' : 'Clique para expandir'}
            </p>
          </div>
        </div>

        {/* Botão Toggle */}
        <button
          className="p-2 hover:bg-gray-700/50 rounded-lg transition-all"
          aria-label={isExpanded ? "Recolher dashboard" : "Expandir dashboard"}
        >
          <ChevronDown 
            className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
              isExpanded ? 'rotate-180' : 'rotate-0'
            }`}
          />
        </button>
      </div>

      {/* Conteúdo Colapsável */}
      <div 
        className={`transition-all duration-300 ease-in-out ${
          isExpanded 
            ? 'max-h-96 opacity-100' 
            : 'max-h-0 opacity-0 pointer-events-none'
        }`}
      >
        <div className="px-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {/* Total de Mesas */}
            <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="text-gray-400 text-xs font-medium">Total</span>
                <Store className="w-4 h-4 text-gray-500" />
              </div>
              <p className="text-white font-bold text-2xl">{stats.total}</p>
              <p className="text-gray-500 text-xs mt-1">mesas</p>
            </div>

            {/* Mesas Livres */}
            <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/30 hover:border-green-500/50 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="text-green-400 text-xs font-medium">Livres</span>
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              </div>
              <p className="text-green-400 font-bold text-2xl">{stats.free}</p>
              <p className="text-green-500/70 text-xs mt-1">disponíveis</p>
            </div>

            {/* Mesas Ocupadas */}
            <div className="bg-orange-500/10 rounded-lg p-3 border border-orange-500/30 hover:border-orange-500/50 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="text-orange-400 text-xs font-medium">Ocupadas</span>
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
              </div>
              <p className="text-orange-400 font-bold text-2xl">{stats.occupied}</p>
              <p className="text-orange-500/70 text-xs mt-1">em atendimento</p>
            </div>

            {/* Taxa de Ocupação */}
            <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30 hover:border-blue-500/50 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="text-blue-400 text-xs font-medium">Ocupação</span>
                <Zap className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-blue-400 font-bold text-2xl">{occupancyRate}%</p>
              <p className="text-blue-500/70 text-xs mt-1">taxa atual</p>
            </div>

            {/* Faturamento Atual */}
            <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30 hover:border-purple-500/50 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="text-purple-400 text-xs font-medium">Faturamento</span>
                <DollarSign className="w-4 h-4 text-purple-500" />
              </div>
              <p className="text-purple-400 font-bold text-xl">R$ {stats.totalRevenue.toFixed(2)}</p>
              <p className="text-purple-500/70 text-xs mt-1">em aberto</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 🆕 Componente de Busca e Filtros
const TableFilters = ({ searchQuery, setSearchQuery, filterStatus, setFilterStatus, onClear }) => {
  const [showFilters, setShowFilters] = useState(false);

  return (
    <div className="space-y-3 mb-4">
      {/* Barra de Busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <input
          type="text"
          placeholder="Buscar mesa por nome ou número..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-11 pr-24 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
          data-testid="table-search-input"
        />
        {searchQuery && (
          <button
            onClick={onClear}
            className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
          >
            <XCircle className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-all ${
            showFilters ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="toggle-filters-button"
        >
          <Filter className="w-4 h-4" />
        </button>
      </div>

      {/* Filtros Rápidos */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 animate-fadeIn">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              filterStatus === 'all'
                ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            data-testid="filter-all"
          >
            Todas
          </button>
          <button
            onClick={() => setFilterStatus('livre')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              filterStatus === 'livre'
                ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            data-testid="filter-livre"
          >
            🟢 Livres
          </button>
          <button
            onClick={() => setFilterStatus('ocupada')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              filterStatus === 'ocupada'
                ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            data-testid="filter-ocupada"
          >
            🟠 Ocupadas
          </button>
          <button
            onClick={() => setFilterStatus('reservada')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              filterStatus === 'reservada'
                ? 'bg-yellow-500 text-white shadow-lg shadow-yellow-500/30'
                : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            data-testid="filter-reservada"
          >
            🟡 Reservadas
          </button>
        </div>
      )}
    </div>
  );
};

export { OccupationTimer, TablesDashboard, TableFilters };
